import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { IMPERSONATION_BANNER_HEIGHT } from './ImpersonationBanner';
import { LanguageSelector } from '../LanguageSelector';
import { ChangelogDialog } from '../ChangelogDialog';
import { HelpDrawer } from '../HelpDrawer';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { TenantSwitcher } from './TenantSwitcher';
import { BugReportButton } from '../bug-report/BugReportButton';
import { TopBarChips } from './TopBarChips';
import { ProjectSwitcher } from './ProjectSwitcher';
import { SIDEBAR_WIDTH } from './Sidebar';
import { QuickCreateOffenerPunkt } from '../common/QuickCreateOffenerPunkt';

// SMSPRO-178-Portierung: Das feste, aus VITE_ENV_BADGE* abgeleitete Umgebungs-
// Badge wurde entfernt. Der plattformweite Umgebungs-Chip (app=null) kommt jetzt
// ueber <TopBarChips /> aus dem BFF — ein zweiter, unverwalteter Weg fuer
// dieselbe Anzeige wuerde sonst zu einem Doppel-Badge fuehren.

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  }
  if (email) return email[0]!.toUpperCase();
  return '?';
}

export function TopBar() {
  const { theme, setTheme, baseThemes, experimentalThemes, experimentalUnlocked } = useThemeMode();
  const { user, logout, isImpersonating } = useAuth();
  const topOffset = isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // Grundthemes immer sichtbar; experimentelle nur nach Freischaltung.
  const visibleThemes = experimentalUnlocked
    ? [...baseThemes, ...experimentalThemes]
    : [...baseThemes];
  // Emoji über alle bekannten Themes auflösen (auch, falls noch ein experimentelles aktiv ist).
  const currentEmoji =
    [...baseThemes, ...experimentalThemes].find((opt) => opt.name === theme)?.emoji ?? '🎨';

  const handleOpenUserMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseUserMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    handleCloseUserMenu();
    logout();
  };

  return (
    <>
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        top: `${topOffset}px`,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ml: `${SIDEBAR_WIDTH}px`,
        width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
        transition: 'top 150ms ease',
      }}
    >
      <Toolbar sx={{ position: 'relative', gap: 1 }}>
        {/* Projekt-Switcher */}
        <ProjectSwitcher />

        {/* Konfigurierbare Kopfzeilen-Chips (inkl. Umgebungs-Hinweis) — zentriert */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        >
          <TopBarChips />
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TenantSwitcher />

          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <LanguageSelector />
          </Box>

          <QuickCreateOffenerPunkt />

          <ChangelogDialog />

          <Tooltip title={t('help.title')}>
            <IconButton size="small" onClick={() => setHelpOpen(true)}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <BugReportButton />

          <Tooltip title={t('theme.select', 'Theme wählen')}>
            <IconButton
              onClick={(e) => setThemeAnchor(e.currentTarget)}
              size="small"
              aria-label={t('theme.select', 'Theme wählen')}
            >
              <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1 }}>{currentEmoji}</Box>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={themeAnchor}
            open={Boolean(themeAnchor)}
            onClose={() => setThemeAnchor(null)}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            slotProps={{ paper: { sx: { minWidth: 180 } } }}
          >
            {visibleThemes.map((opt) => (
              <MenuItem
                key={opt.name}
                selected={opt.name === theme}
                onClick={() => { setTheme(opt.name); setThemeAnchor(null); }}
              >
                <ListItemIcon>
                  <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1 }}>{opt.emoji}</Box>
                </ListItemIcon>
                <ListItemText>{opt.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>

          {user && (
            <>
              <Tooltip title={user.name}>
                <IconButton onClick={handleOpenUserMenu} size="small" sx={{ ml: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(user.name, user.email)}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseUserMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{ paper: { sx: { minWidth: 200 } } }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{user.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/profile'); }}>
                  <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('userMenu.profile')}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('userMenu.logout')}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
    <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
